/**
 * Build the deduplicated guest list of a trip, out of the travelers attached to
 * each booking's rooms and experiences.
 *
 * Travelers are stored per booking line, so the same guest carries a different
 * id in a room and in an experience: they are matched on their details instead.
 * Travelers saved without a name — folders can now be saved before the details
 * are filled in — all share the same details, so they are additionally matched
 * on their rank among the identical travelers of the same booking line.
 * Without that rank every nameless traveler after the first would be dropped.
 */
const detailsKey = (traveler) => {
  const { firstName, lastName, birthdate, gender } = traveler ?? {};
  return `${firstName ?? ''}_${lastName ?? ''}_${birthdate ?? ''}_${gender ?? ''}`;
};

const hasName = (traveler) =>
  Boolean(traveler?.firstName?.trim() || traveler?.lastName?.trim());

const guestKey = (traveler, ranks) => {
  const details = detailsKey(traveler);
  if (hasName(traveler)) return details;

  const rank = (ranks.get(details) ?? 0) + 1;
  ranks.set(details, rank);
  return `${details}#${rank}`;
};

export function collectTripGuests(trip) {
  const seen = new Set();

  return (trip?.bookings ?? []).reduce((acc, booking) => {
    // One rank counter per source: a nameless traveler listed both in a room
    // and in an experience of the same booking is the same person.
    const experienceRanks = new Map();
    const roomRanks = new Map();

    const collect = (travelers, ranks) => {
      (travelers ?? []).forEach((traveler) => {
        const key = guestKey(traveler, ranks);
        if (!seen.has(key)) {
          seen.add(key);
          acc.push(traveler);
        }
      });
    };

    (booking?.experiencesBooked ?? []).forEach((experience) =>
      collect(experience?.travelers, experienceRanks),
    );
    (booking?.roomsBooked ?? []).forEach((room) =>
      collect(room?.travelers, roomRanks),
    );

    return acc;
  }, []);
}
